import { motion } from "motion/react";
import Header from "@/components/Header";
import { Footer2 } from "@/components/ui/shadcnblocks-com-footer2";
import { supabase } from "@/integrations/supabase/client";
import { useWishlistStore } from "@/store/wishlistStore";
import { useCartStore } from "@/store/cartStore";
import ShopifyProductCard from "@/components/ShopifyProductCard";
import { Button } from "@/components/ui/button";
import { Link, useSearchParams } from "react-router-dom";
import { Heart, Share2, Copy, Check, ShoppingCart } from "lucide-react";
import PageTransition from "@/components/PageTransition";
import { alertToast } from "@/contexts/AlertToastContext";
import { useEffect, useState } from "react";
import { fetchProductByHandle } from "@/lib/shopify";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { SEO } from "@/components/SEO";
import { useStaticTranslation } from "@/lib/staticTranslations";

const Wishlist = () => {
  const { t: seoT } = useStaticTranslation("seo");
  const { items, localItems, clearWishlist, addItem, removeItem, loadWishlist } = useWishlistStore();
  const addToCart = useCartStore((state) => state.addItem);
  
  // Combine server and local items
  const allItems = [...items, ...localItems];
  const [searchParams] = useSearchParams();
  const [isShareDialogOpen, setIsShareDialogOpen] = useState(false);
  const [isCopied, setIsCopied] = useState(false);
  const [isLoadingShared, setIsLoadingShared] = useState(false);
  const [isAddingAllToCart, setIsAddingAllToCart] = useState(false);

  // Load wishlist on mount
  useEffect(() => {
    loadWishlist();
  }, [loadWishlist]);

  // Handle shared wishlist from URL
  useEffect(() => {
    const loadSharedData = async () => {
      const sharedData = searchParams.get('shared');
      
      if (sharedData) {
        setIsLoadingShared(true);
        try {
          // Use decodeURIComponent for Unicode-safe decoding
          const productHandles = JSON.parse(decodeURIComponent(sharedData));
          
          if (!Array.isArray(productHandles) || productHandles.length === 0) {
            throw new Error('Invalid shared data');
          }
          
          // Load each product and add to wishlist
          const loadedProducts = await Promise.all(
            productHandles.map(async (handle: string) => {
              if (!handle) return null;
              return await fetchProductByHandle(handle);
            })
          );

          let addedCount = 0;
          const { data: { session } } = await supabase.auth.getSession();
          
          for (const product of loadedProducts) {
            if (product) {
              if (session) {
                const added = await addItem(product);
                if (added) addedCount++;
              } else {
                // For non-authenticated users, add all products locally at once
                const { localItems, items } = useWishlistStore.getState();
                const existsLocal = localItems.some(item => item.id === product.id || item.handle === product.handle);
                const existsServer = items.some(item => item.id === product.id || item.handle === product.handle);
                if (!existsLocal && !existsServer) {
                  useWishlistStore.getState().addItemLocal(product);
                  addedCount++;
                }
              }
            }
          }

          if (addedCount > 0) {
            alertToast.success(`${addedCount} מוצרים נוספו לרשימת המועדפים!`);
          } else {
            alertToast.info('המוצרים כבר קיימים ברשימת המועדפים שלך');
          }
          
          // Clear the URL parameter
          window.history.replaceState({}, '', '/wishlist');
        } catch (error) {
          console.error('Failed to load shared wishlist:', error);
          alertToast.error('שגיאה בטעינת רשימת המועדפים המשותפת');
        } finally {
          setIsLoadingShared(false);
        }
      }
    };

    loadSharedData();
  }, [searchParams, addItem]);

  const handleShare = () => {
    setIsShareDialogOpen(true);
  };

  const generateShareLink = () => {
    if (allItems.length === 0) return window.location.origin + '/wishlist';
    const productHandles = allItems.map(item => item.handle).filter(Boolean);
    if (productHandles.length === 0) return window.location.origin + '/wishlist';
    // Use encodeURIComponent for Unicode-safe encoding
    const encoded = encodeURIComponent(JSON.stringify(productHandles));
    return `${window.location.origin}/wishlist?shared=${encoded}`;
  };

  const copyShareLink = async () => {
    const link = generateShareLink();
    try {
      await navigator.clipboard.writeText(link);
      setIsCopied(true);
      alertToast.success('הקישור הועתק ללוח!');
      setTimeout(() => setIsCopied(false), 2000);
    } catch (error) {
      alertToast.error('שגיאה בהעתקת הקישור');
    }
  };

  const shareViaWebShare = async () => {
    const link = generateShareLink();
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'המוצרים המועדפים שלי',
          text: `בדוק את רשימת המוצרים המועדפים שלי מחנות Alice`,
          url: link,
        });
      } catch (error) {
        console.error('Error sharing:', error);
      }
    } else {
      copyShareLink();
    }
  };

  const handleAddAllToCart = async () => {
    if (allItems.length === 0) return;
    
    setIsAddingAllToCart(true);
    
    try {
      let addedCount = 0;
      
      // Add each item to cart - need to fetch full product data for variants
      for (const product of allItems) {
        try {
          // Fetch full product data to get variants
          const fullProduct = await fetchProductByHandle(product.handle);
          
          if (!fullProduct) {
            console.warn(`Could not fetch product: ${product.handle}`);
            continue;
          }
          
          const defaultVariant = fullProduct.variants?.edges?.[0]?.node;
          
          if (defaultVariant && defaultVariant.availableForSale) {
            addToCart({
              product: fullProduct,
              variantId: defaultVariant.id,
              variantTitle: defaultVariant.title,
              price: {
                amount: defaultVariant.price.amount,
                currencyCode: defaultVariant.price.currencyCode,
              },
              quantity: 1,
            });
            
            addedCount++;
            
            // Remove from wishlist
            removeItem(product.id);
          }
        } catch (err) {
          console.error(`Error processing product ${product.handle}:`, err);
        }
      }
      
      if (addedCount > 0) {
        alertToast.success(`${addedCount} מוצרים הועברו לסל הקניות!`);
      } else {
        alertToast.error('לא ניתן להעביר מוצרים לסל');
      }
    } catch (error) {
      console.error('Error adding all to cart:', error);
      alertToast.error('שגיאה בהעברת המוצרים לסל');
    } finally {
      setIsAddingAllToCart(false);
    }
  };

  return (
    <PageTransition>
      <SEO
        title={seoT("wishlistTitle", "רשימת מועדפים")}
        description={seoT("wishlistDescription", "רשימת המועדפים שלך באליס תכשיטים - שמור את התכשיטים האהובים עליך ושתף עם חברים ומשפחה")}
        canonical="https://alice.jewelry/wishlist"
        noindex={true}
      />
      <div className="min-h-screen bg-background flex flex-col" dir="rtl">
        <Header />
        
        <main className="flex-1">
          <div className="container mx-auto px-[25px] md:px-[50px] lg:px-[150px] py-8 md:py-12">
            {/* Breadcrumb */}
            <div className="mb-6">
              <Breadcrumb>
                <BreadcrumbList>
                  <BreadcrumbItem>
                    <BreadcrumbLink href="/">ראשי</BreadcrumbLink>
                  </BreadcrumbItem>
                  <BreadcrumbSeparator />
                  <BreadcrumbItem>
                    <BreadcrumbPage>מועדפים</BreadcrumbPage>
                  </BreadcrumbItem>
                </BreadcrumbList>
              </Breadcrumb>
            </div>

            {/* Header */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="text-center mb-12"
            >
              <div className="gold-divider mb-6" />
              <div className="flex items-center justify-center gap-3 mb-4">
                
                <h1 className="text-4xl font-serif">המוצרים המועדפים שלי</h1>
              </div>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                {allItems.length > 0 
                  ? `יש לך ${allItems.length} ${allItems.length === 1 ? 'מוצר מועדף' : 'מוצרים מועדפים'}`
                  : 'עדיין לא הוספת מוצרים למועדפים'}
              </p>
              
              {allItems.length > 0 && (
                <div className="flex flex-wrap items-center justify-center gap-3 mt-6">
                  <Button
                    onClick={handleAddAllToCart}
                    disabled={isAddingAllToCart}
                    className="bg-primary hover:bg-primary/90 gap-2"
                  >
                    <ShoppingCart className="h-4 w-4" />
                    {isAddingAllToCart ? 'מעביר לסל...' : 'העבר הכל לסל'}
                  </Button>
                  <Button
                    onClick={handleShare}
                    variant="outline"
                    className="gap-2"
                  >
                    <Share2 className="h-4 w-4" />
                    שתף רשימה
                  </Button>
                  <Button
                    variant="outline"
                    onClick={clearWishlist}
                  >
                    נקה את כל המועדפים
                  </Button>
                </div>
              )}
            </motion.div>

            {/* Loading State */}
            {isLoadingShared ? (
              <div className="flex items-center justify-center py-16">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
              </div>
            ) : allItems.length === 0 ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="text-center py-16"
              >
                <div className="max-w-md mx-auto bg-muted/30 rounded-lg p-12">
                  <Heart className="h-16 w-16 text-muted-foreground/40 mx-auto mb-6" />
                  <h2 className="text-2xl font-serif mb-4 text-foreground">
                    הרשימה שלך ריקה
                  </h2>
                  <p className="text-muted-foreground mb-8">
                    התחל לסמן מוצרים שאתה אוהב כדי למצוא אותם בקלות מאוחר יותר
                  </p>
                  <Button asChild className="bg-primary hover:bg-primary/90">
                    <Link to="/">המשך לקנות</Link>
                  </Button>
                </div>
              </motion.div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 [1650px]:grid-cols-5 gap-6">
                {allItems.map((product, index) => (
                  <motion.div
                    key={product.id}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.5, delay: index * 0.08 }}
                  >
                    <ShopifyProductCard product={product} />
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        </main>

        <Footer2 />
      </div>

      {/* Share Dialog */}
      <Dialog open={isShareDialogOpen} onOpenChange={setIsShareDialogOpen}>
        <DialogContent className="sm:max-w-md" dir="rtl">
          <DialogHeader>
            <DialogTitle>שתף את רשימת המועדפים שלך</DialogTitle>
            <DialogDescription>
              שתף את המוצרים המועדפים שלך עם משפחה וחברים
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex items-center gap-2 p-3 bg-muted rounded-lg">
              <input
                type="text"
                value={generateShareLink()}
                readOnly
                className="flex-1 bg-transparent text-sm outline-none"
              />
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <Button
                onClick={copyShareLink}
                className="flex-1 gap-2"
                variant={isCopied ? "outline" : "default"}
              >
                {isCopied ? (
                  <>
                    <Check className="h-4 w-4" />
                    הועתק!
                  </>
                ) : (
                  <>
                    <Copy className="h-4 w-4" />
                    העתק קישור
                  </>
                )}
              </Button>
              {navigator.share && (
                <Button
                  onClick={shareViaWebShare}
                  className="flex-1 gap-2"
                  variant="outline"
                >
                  <Share2 className="h-4 w-4" />
                  שתף
                </Button>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </PageTransition>
  );
};

export default Wishlist;
